// label-interaction.js

// Константы
const TEXT_WIDTH_COEFF = 0.8;
const MIN_FONT_SIZE = 6;
const TYPE_SPEED_DEFAULT = 40;
const TYPE_SPEED_SINGLE = 30;
const HOLD_CLICK = 1800;
const HOLD_HOVER = 1200;
const HOLD_SOLO = 1500;
const GAZE_INTERVAL_MS = 100;
const LOOP_DELAY_BUSY_MIN = 4000;
const LOOP_DELAY_BUSY_MAX = 9000;
const LOOP_DELAY_MIN = 5000;
const LOOP_DELAY_MAX = 12000;

import { getDialogues } from './label-dialogues.js';

document.addEventListener('DOMContentLoaded', async () => {
  let dialoguesData;
  try {
    dialoguesData = await getDialogues();
  } catch {
    return;
  }

  const {
    selfDialogues = [],
    betweenDialogues = [],
    soloPhrases = [],
    clickPhrases = [],
    hoverPhrases = []
  } = dialoguesData;

  const buttons = Array.from(document.querySelectorAll('svg.choosebutton'));
  if (!buttons.length) return;

  let interactionBusy = false;
  const busyButtons = new Set();

  const randInt = (m, n) => Math.floor(Math.random() * (n - m + 1)) + m;
  const delay = ms => new Promise(r => setTimeout(r, ms));

  function splitLines(phrase, textElem, maxWidth) {
    if (Array.isArray(phrase)) {
      return phrase;
    }
    if (typeof phrase === 'string' && phrase.indexOf('\n') !== -1) {
      return phrase.split('\n');
    }
    while (textElem.firstChild) {
      textElem.removeChild(textElem.firstChild);
    }
    const tmpSpan = document.createElementNS(textElem.namespaceURI, 'tspan');
    tmpSpan.setAttribute('x', '50%');
    tmpSpan.setAttribute('text-anchor', 'middle');
    tmpSpan.textContent = phrase;
    textElem.appendChild(tmpSpan);
    let bbox = tmpSpan.getBBox();
    if (bbox.width <= maxWidth) {
      textElem.removeChild(tmpSpan);
      return [phrase];
    }
    const words = phrase.split(' ');
    let bestDiff = Infinity;
    let result = [phrase];
    for (let i = 1; i < words.length; i++) {
      const line1 = words.slice(0, i).join(' ');
      const line2 = words.slice(i).join(' ');
      tmpSpan.textContent = line1;
      const w1 = tmpSpan.getBBox().width;
      tmpSpan.textContent = line2;
      const w2 = tmpSpan.getBBox().width;
      if (w1 <= maxWidth && w2 <= maxWidth) {
        const diff = Math.abs(w1 - w2);
        if (diff < bestDiff) {
          bestDiff = diff;
          result = [line1, line2];
        }
      }
    }
    textElem.removeChild(tmpSpan);
    return result;
  }

  async function fit(textElem, maxWidth) {
    await delay(0);
    let fontSize = parseFloat(getComputedStyle(textElem).fontSize);
    let bbox = textElem.getBBox();
    while (bbox.width > maxWidth && fontSize > MIN_FONT_SIZE) {
      fontSize -= 1;
      textElem.style.fontSize = fontSize + 'px';
      await delay(0);
      bbox = textElem.getBBox();
    }
  }

  async function typeLines(textElem, lines, speed = TYPE_SPEED_DEFAULT) {
    while (textElem.firstChild) textElem.removeChild(textElem.firstChild);
    const n = lines.length;
    const spans = lines.map((_, i) => {
      const tspan = document.createElementNS(textElem.namespaceURI, 'tspan');
      tspan.setAttribute('x', '50%');
      tspan.setAttribute('text-anchor', 'middle');
      if (i === 0) {
        const offset = -((n - 1) * 0.5);
        tspan.setAttribute('dy', `${offset}em`);
      } else {
        tspan.setAttribute('dy', '1em');
      }
      textElem.appendChild(tspan);
      return tspan;
    });
    for (let i = 0; i < lines.length; i++) {
      for (const ch of lines[i]) {
        spans[i].textContent += ch;
        await delay(speed);
      }
    }
  }

  function store(btn) {
    const txt = btn.querySelector('text');
    if (!txt || btn.dataset.orig) return;
    btn.dataset.orig = txt.textContent;
    btn.dataset.font = getComputedStyle(txt).fontSize;
  }

  function restore(btn) {
    const txt = btn.querySelector('text');
    if (!txt || !btn.dataset.orig) return;
    txt.textContent = btn.dataset.orig;
    txt.style.fontSize = btn.dataset.font;
    delete btn.dataset.orig;
    delete btn.dataset.font;
  }

  async function singlePhrase(btn, phrase, hold = HOLD_SOLO) {
    if (interactionBusy || busyButtons.has(btn)) return;
    interactionBusy = true;
    busyButtons.add(btn);
    store(btn);
    const txt = btn.querySelector('text');
    if (!txt) {
      busyButtons.delete(btn);
      interactionBusy = false;
      return;
    }
    txt.style.fontSize = btn.dataset.font;
    const maxW = btn.getBBox().width * TEXT_WIDTH_COEFF;
    const lines = splitLines(phrase, txt, maxW);
    await typeLines(txt, lines, TYPE_SPEED_SINGLE);
    await fit(txt, maxW);
    await delay(hold);
    restore(btn);
    busyButtons.delete(btn);
    interactionBusy = false;
  }

  buttons.forEach(btn => {
    btn.addEventListener('click', () => {
      if (interactionBusy || busyButtons.has(btn)) return;
      btn.dispatchEvent(new Event('conversationStart'));
      const phrase = clickPhrases[randInt(0, clickPhrases.length - 1)];
      if (!phrase) return;
      singlePhrase(btn, phrase, HOLD_CLICK);
    }, { passive: true });

    btn.addEventListener('mouseenter', () => {
      if (interactionBusy || busyButtons.has(btn)) return;
      btn.dispatchEvent(new Event('conversationStart'));
      const phrase = hoverPhrases[randInt(0, hoverPhrases.length - 1)];
      if (!phrase) return;
      singlePhrase(btn, phrase, HOLD_HOVER);
    }, { passive: true });
  });

  function nearest(of) {
    const rect = of.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let minDist = Infinity;
    let nearestBtn = null;
    buttons.forEach(b => {
      if (b === of) return;
      const r = b.getBoundingClientRect();
      const dx = r.left + r.width / 2 - cx;
      const dy = r.top + r.height / 2 - cy;
      const dist = dx * dx + dy * dy;
      if (dist < minDist) {
        minDist = dist;
        nearestBtn = b;
      }
    });
    return nearestBtn;
  }

  async function dialogue(set, a, b) {
    if (!a || !b) return;
    interactionBusy = true;
    store(a);
    store(b);
    a.dispatchEvent(new Event('conversationStart'));
    b.dispatchEvent(new Event('conversationStart'));
    let gazeInterval;
    try {
      if (typeof window.updateEyes === 'function') {
        gazeInterval = setInterval(() => {
          const ra = a.getBoundingClientRect();
          const rb = b.getBoundingClientRect();
          window.updateEyes(a, rb.left + rb.width / 2, rb.top + rb.height / 2);
          window.updateEyes(b, ra.left + ra.width / 2, ra.top + ra.height / 2);
        }, GAZE_INTERVAL_MS);
      }
      for (const { ask, answer } of set) {
        const ta = a.querySelector('text');
        const tb = b.querySelector('text');
        if (!ta || !tb) break;
        ta.style.fontSize = a.dataset.font;
        tb.style.fontSize = b.dataset.font;
        const mwA = a.getBBox().width * TEXT_WIDTH_COEFF;
        const mwB = b.getBBox().width * TEXT_WIDTH_COEFF;

        const askLines = splitLines(ask, ta, mwA);
        await typeLines(ta, askLines);
        await fit(ta, mwA);
        await delay(500);

        const ansLines = splitLines(answer, tb, mwB);
        await typeLines(tb, ansLines);
        await fit(tb, mwB);
        await delay(1300);
      }
    } finally {
      if (gazeInterval) clearInterval(gazeInterval);
      await delay(700);
      restore(a);
      restore(b);
      interactionBusy = false;
    }
  }

  async function selfDialog(set, btn) {
    if (!btn) return;
    interactionBusy = true;
    store(btn);
    btn.dispatchEvent(new Event('conversationStart'));
    const txt = btn.querySelector('text');
    if (!txt) {
      interactionBusy = false;
      return;
    }
    txt.style.fontSize = btn.dataset.font;
    const mw = btn.getBBox().width * TEXT_WIDTH_COEFF;
    for (const { ask, answer } of set) {
      const askLines = splitLines(ask, txt, mw);
      await typeLines(txt, askLines);
      await fit(txt, mw);
      await delay(500);

      const ansLines = splitLines(answer, txt, mw);
      await typeLines(txt, ansLines);
      await fit(txt, mw);
      await delay(1300);
    }
    restore(btn);
    interactionBusy = false;
  }

  const loop = async () => {
    if (interactionBusy) return setTimeout(loop, randInt(LOOP_DELAY_BUSY_MIN, LOOP_DELAY_BUSY_MAX));
    const roll = Math.random();
    if (buttons.length > 1 && betweenDialogues.length && roll < 0.5) {
      const a = buttons[randInt(0, buttons.length - 1)];
      const b = nearest(a);
      const d = betweenDialogues[randInt(0, betweenDialogues.length - 1)];
      if (d && b) await dialogue([d], a, b);
    } else if (roll < 0.8) {
      const btn = buttons[randInt(0, buttons.length - 1)];
      const d = selfDialogues[randInt(0, selfDialogues.length - 1)];
      if (d) await selfDialog([d], btn);
    } else {
      const btn = buttons[randInt(0, buttons.length - 1)];
      const phrase = soloPhrases[randInt(0, soloPhrases.length - 1)];
      if (phrase) await singlePhrase(btn, phrase, HOLD_SOLO);
    }
    setTimeout(loop, randInt(LOOP_DELAY_MIN, LOOP_DELAY_MAX));
  };

  setTimeout(loop, randInt(LOOP_DELAY_MIN, LOOP_DELAY_MAX));
});
